import React, {useEffect, useState} from 'react';
import './Reviews.css';
import manualReviews, {googleReviewsUrl} from '../config/reviews';

// Liefert die Cloud Function "googleReviews" (Hosting-Rewrite in firebase.json).
const GOOGLE_REVIEWS_ENDPOINT = '/api/google-reviews';
const MAX_REVIEWS = 3;

// Nur im Entwicklungsmodus sichtbar, wenn weder Google-Daten noch
// eigene Stimmen in src/config/reviews.js vorhanden sind.
const placeholders = [1, 2, 3].map((n) => ({
  text: 'Platzhalter: Hier steht später ein kurzes Zitat einer Angehörigen oder eines Kunden, zwei bis drei Sätze.',
  author: `Name ${n}`,
  context: 'Beziehung, Ort',
  placeholder: true
}));

const fromManual = manualReviews.map((r) => ({text: r.quote, author: r.name, context: r.context}));

const formatRating = (value) => value.toLocaleString('de-DE', {minimumFractionDigits: 1, maximumFractionDigits: 1});

const Stars = ({rating}) => (
  <span className="review__stars" role="img" aria-label={`${rating} von 5 Sternen`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} aria-hidden="true" className={n <= Math.round(rating) ? 'is-on' : ''}>★</span>
    ))}
  </span>
);

const useGoogleReviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (typeof fetch !== 'function') return undefined;
    const controller = new AbortController();
    fetch(GOOGLE_REVIEWS_ENDPOINT, {signal: controller.signal})
      .then((res) => (res.ok && /json/.test(res.headers.get('content-type') || '') ? res.json() : null))
      .then((json) => {
        if (json && Array.isArray(json.reviews) && json.reviews.length) setData(json);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  return data;
};

const Reviews = () => {
  const google = useGoogleReviews();

  let items = [];
  if (google) items = google.reviews;
  else if (fromManual.length) items = fromManual;
  else if (process.env.NODE_ENV === 'development') items = placeholders;

  const withText = items.filter((r) => r.text).slice(0, MAX_REVIEWS);
  if (!withText.length) return null;

  const profileUrl = google?.url || googleReviewsUrl;

  return (
    <section className="reviews" aria-labelledby="reviews-title">
      <div className="reviews__inner">
        <div className="reviews__head">
          <div>
            <p className="kicker">Erfahrungen</p>
            <h2 id="reviews-title" className="reviews__title">Das sagen Angehörige und Kunden</h2>
          </div>
          {google?.rating && (
            <p className="reviews__summary">
              <strong>{formatRating(google.rating)}</strong>
              <Stars rating={google.rating} />
              <span>{google.count} {google.count === 1 ? 'Bewertung' : 'Bewertungen'} auf Google</span>
            </p>
          )}
        </div>

        {withText.length > 0 && (
          <ul className="reviews__grid">
            {withText.map((r, index) => (
              <li key={`${r.author}-${index}`} className={`review ${r.placeholder ? 'review--placeholder' : ''}`}>
                <figure>
                  {r.rating ? <Stars rating={r.rating} /> : null}
                  <blockquote className="review__quote">
                    <p>{r.text}</p>
                  </blockquote>
                  <figcaption className="review__author">
                    {r.authorUrl ? (
                      <a href={r.authorUrl} target="_blank" rel="noreferrer"><strong>{r.author}</strong></a>
                    ) : (
                      <strong>{r.author}</strong>
                    )}
                    {r.relativeTime && <span>{r.relativeTime} auf Google</span>}
                    {r.context && <span>{r.context}</span>}
                  </figcaption>
                </figure>
              </li>
            ))}
          </ul>
        )}

        {profileUrl && (
          <a className="reviews__google" href={profileUrl} target="_blank" rel="noreferrer">
            Alle Bewertungen auf Google ansehen
          </a>
        )}
      </div>
    </section>
  );
};

export default Reviews;
